import React from 'react';
import { Box, Typography } from '@mui/material';

/*
 * Información de "Autogestión de Materiales TALSA" (P-CS-CP-07 / POL-CS-04).
 * Diseñado para ir sobre el panel azul oscuro del login.
 */

const C = {
    blanco: '#ffffff',
    texto: 'rgba(255,255,255,0.72)',
    tenue: 'rgba(255,255,255,0.5)',
    linea: 'rgba(255,255,255,0.12)',
    fondo: 'rgba(255,255,255,0.05)',
    rojo: '#EF5350',
    verde: '#66BB6A',
};

const MONO = '"IBM Plex Mono", "Consolas", "Menlo", monospace';

const Codigo = ({ children }) => (
    <Box component="span" sx={{
        fontFamily: MONO, fontWeight: 600, fontSize: '0.85em',
        px: 0.6, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.1)',
        color: C.blanco, whiteSpace: 'nowrap',
    }}>
        {children}
    </Box>
);

const Titulo = ({ children }) => (
    <Typography component="h2" sx={{ color: C.blanco, fontWeight: 600, fontSize: '1.15rem', mb: 0.75 }}>
        {children}
    </Typography>
);

const Subtitulo = ({ children }) => (
    <Typography component="h3" sx={{ color: C.blanco, fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
        {children}
    </Typography>
);

const Item = ({ titulo, children }) => (
    <Box component="li" sx={{ color: C.texto, fontSize: '0.92rem', lineHeight: 1.45 }}>
        <Box component="span" sx={{ color: C.blanco, fontWeight: 600 }}>{titulo}:</Box>{' '}
        {children}
    </Box>
);

const Lista = ({ columnas, children, sx }) => (
    <Box component="ul" sx={{
        m: 0, p: 0, listStyle: 'none', display: 'grid', columnGap: 2.5, rowGap: 0.75,
        gridTemplateColumns: columnas ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
        ...sx,
    }}>
        {children}
    </Box>
);

const Columna = ({ si, titulo, nota, aviso, children }) => (
    <Box sx={{ borderTop: `3px solid ${si ? C.verde : C.rojo}`, pt: 1 }}>
        <Typography sx={{ color: C.blanco, fontWeight: 600, fontSize: '1.05rem' }}>{titulo}</Typography>
        {nota && (
            <Typography sx={{ color: C.tenue, fontSize: '0.88rem', mt: 0.2, mb: 0.75 }}>{nota}</Typography>
        )}
        {aviso && (
            <Box sx={{
                mt: 0.5, mb: 0.75, px: 1.25, py: 0.6,
                bgcolor: 'rgba(239,83,80,0.12)', borderLeft: `3px solid ${C.rojo}`, borderRadius: '0 6px 6px 0',
                color: C.blanco, fontSize: '0.88rem', lineHeight: 1.4,
            }}>
                {aviso}
            </Box>
        )}
        <Lista>{children}</Lista>
    </Box>
);

const Pieza = ({ children }) => (
    <Box component="span" sx={{
        display: 'inline-block', px: 0.9, py: 0.25, borderRadius: '5px',
        border: `1px solid ${C.linea}`, bgcolor: C.fondo,
        color: C.blanco, fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.4,
    }}>
        {children}
    </Box>
);

const Separador = ({ children }) => (
    <Box component="span" sx={{ color: C.tenue, fontSize: '0.85rem', px: 0.25 }}>{children}</Box>
);

const Campo = ({ etiqueta, max, guia, children }) => (
    <Box sx={{ borderLeft: `2px solid ${C.linea}`, pl: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, mb: 0.5 }}>
            <Typography sx={{ color: C.blanco, fontWeight: 600, fontSize: '0.92rem' }}>{etiqueta}</Typography>
            <Typography sx={{ color: C.tenue, fontSize: '0.82rem' }}>Máximo {max} caracteres</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            <Box component="span" sx={{ color: C.texto, fontSize: '0.85rem', mr: 0.25 }}>{guia}</Box>
            {children}
        </Box>
    </Box>
);

const Ejemplo = () => {
    const malo = [
        { t: 'Rodamiento 6205' }, { t: ',', x: true }, { t: ' 25' }, { t: ',', x: true },
        { t: '4 mm ' }, { t: '@', x: true }, { t: ' caja' },
    ];
    return (
        <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: 'auto 1fr', md: 'auto 1fr auto 1fr' },
            columnGap: 1.5, rowGap: 0.75,
            alignItems: 'center', mt: 0.75, px: 1.5, py: 0.6, borderRadius: '8px', bgcolor: C.fondo,
        }}>
            <Box component="span" aria-label="Incorrecto">❌</Box>
            <Box sx={{ fontFamily: MONO, fontSize: '0.9rem', color: C.tenue }}>
                {malo.map((p, i) => p.x ? (
                    <Box key={i} component="span" sx={{
                        color: C.rojo, fontWeight: 700, bgcolor: 'rgba(239,83,80,0.18)', px: '1px',
                    }}>{p.t}</Box>
                ) : <span key={i}>{p.t}</span>)}
            </Box>
            <Box component="span" aria-label="Correcto">✅</Box>
            <Box sx={{ fontFamily: MONO, fontSize: '0.9rem', color: C.blanco }}>
                RODAMIENTO 6205 25.4 MM CAJA
            </Box>
        </Box>
    );
};

export default function PoliticasMatricula() {
    return (
        <Box sx={{ width: '100%', maxWidth: 900, textAlign: 'left' }}>
            <Typography component="h1" sx={{
                color: C.blanco, fontWeight: 700, fontSize: { xs: '1.6rem', md: '1.9rem' },
                lineHeight: 1.2, mb: 1
            }}>
                🚀 Autogestión de Materiales TALSA
            </Typography>

            {/* Qué se crea aquí / qué no */}
            <Box component="section" sx={{
                mb: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3,
            }}>
                <Columna
                    si
                    titulo="✅ ¿Qué puedes crear en esta plataforma?"
                    nota="Este aplicativo está habilitado exclusivamente para los siguientes casos:"
                >
                    <Item titulo="Repuestos, accesorios y consumibles">
                        Que no requieran número de serie, unidades de conversión ni gestión de lotes.
                    </Item>
                    <Item titulo="Maquinaria y Equipo">
                        Cuyos códigos no inicien con las letras <Codigo>U</Codigo> <Codigo>A</Codigo> <Codigo>R</Codigo> o <Codigo>MP</Codigo>.
                    </Item>
                </Columna>
                <Columna
                    titulo="🚫 ¿Qué NO se gestiona aquí? (Vía Gestor Documental)"
                    aviso={<>
                        <b>No se crean aquí.</b> Siguen el proceso tradicional en el <b>Gestor Documental</b> con
                        los siguientes formatos:
                        <Box component="ul" sx={{ m: 0, mt: 0.5, p: 0, listStyle: 'none', display: 'grid', gap: 0.4 }}>
                            <li><Codigo>F-CS-CP-17</Codigo> Solicitud de Matrícula de Materiales</li>
                            <li><Codigo>F-CS-CP-31</Codigo> Solicitud de matrícula de gastos y servicios</li>
                        </Box>
                    </>}
                >
                    <Item titulo="Productos Químicos">
                        Incluye artículos de limpieza, insecticidas, lubricantes, pinturas, solventes, etc. Requieren indispensablemente una Ficha de Seguridad con una vigencia inferior a 5 años.
                    </Item>
                    <Item titulo="Materiales con Logística Compleja">
                        Equipos cuyos códigos inicien con las letras <Codigo>U</Codigo> <Codigo>A</Codigo> <Codigo>R</Codigo> o <Codigo>MP</Codigo>; así como accesorios, consumibles o repuestos que requieran configuración de lotes, series o unidades de conversión.
                    </Item>
                </Columna>
            </Box>

            {/* Políticas de Matrícula */}
            <Box component="section" sx={{ pt: 1.5, borderTop: `1px solid ${C.linea}` }}>
                <Titulo>
                    🛡️ Políticas de Matrícula{' '}
                    <Box component="span" sx={{ color: C.tenue, fontWeight: 400 }}>(P-CS-CP-07 / POL-CS-04)</Box>
                </Titulo>

                <Box sx={{ mb: 1.25 }}>
                    <Subtitulo>1. Validación y Soportes 📋</Subtitulo>
                    <Lista columnas>
                        <Item titulo="Verificación de Existencia">
                            Es obligatorio validar en el ERP que el material no exista previamente. No se permiten registros duplicados.
                        </Item>
                        <Item titulo="Soportes Documentales">
                            Toda creación debe contar con un respaldo digital (cotizaciones, proformas, despieces o listas de precios).
                        </Item>
                    </Lista>
                </Box>

                <Box sx={{ mb: 1.25 }}>
                    <Subtitulo>2. Escritura y Formato ✍️</Subtitulo>
                    <Lista columnas>
                        <Item titulo="Idioma">Las descripciones deben estar obligatoriamente en <b>ESPAÑOL</b>.</Item>
                        <Item titulo="Tipografía">Uso exclusivo de letras <b>MAYÚSCULAS</b>.</Item>
                        <Item titulo="Caracteres Prohibidos">No utilizar comas (<b>,</b>) ni el símbolo arroba (<b>@</b>).</Item>
                        <Item titulo="Decimales">Utilizar únicamente el punto (<b>.</b>) como separador decimal.</Item>
                    </Lista>
                    <Ejemplo />
                </Box>

                <Box>
                    <Subtitulo>3. Estructura de las Descripciones 📏</Subtitulo>
                    <Box sx={{
                        display: 'grid', columnGap: 2.5, rowGap: 1.25,
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1.3fr' },
                    }}>
                        <Campo etiqueta="Descripción Corta (Nacional)" max={40} guia="Estructura:">
                            <Pieza>Nombre del Ítem</Pieza>
                            <Separador>–</Separador>
                            <Pieza>Características Técnicas</Pieza>
                        </Campo>
                        <Campo etiqueta="Descripción Corta (Importado)" max={40} guia="Estructura:">
                            <Pieza>Nombre del Ítem</Pieza>
                            <Separador>–</Separador>
                            <Pieza>Número de pieza del Proveedor</Pieza>
                        </Campo>
                        <Campo etiqueta="Descripción Detallada" max={100} guia="Debe incluir de forma clara:">
                            {['Medidas', 'Color', 'Voltaje', 'Capacidad', 'Unidad de empaque', 'Componentes'].map((x) => (
                                <Pieza key={x}>{x}</Pieza>
                            ))}
                        </Campo>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
